import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  getHospitalOrganizationById,
  updateHospitalOrganization,
  checkHospitalEmailExists,
  checkHospitalPhoneExists,
  checkHospitalNameExists,
} from '../services/hospitalOrganizationService';
import { getActiveSubscription, updateSubscriptionHistoryGst, updateSubscriptionHistoryOpdConsultationWaiver } from '../services/subscriptionHistoryService';
import {
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  HOSPITAL_FORM_DIALOG_PAPER_SX,
  FIT_CONTENT_DIALOG_CONTENT_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FIELD_PROPS,
} from '../utils/formModalStyles';
import FormSectionHeader from './FormSectionHeader';
import { checkAdminEmailExists, checkAdminMobileExists, updatePortalUser } from '../services/userManagementService';
import { getAllStaff, getStaffDetailByUserId, updateStaffByUserId } from '../services/StaffService';
import { syncSessionUserAfterHospitalEdit } from '../utils/organizationUtils';

/** Coerce API values (e.g. numeric mobile) to trimmed strings for comparisons. */
const toTrimmedString = (value) => String(value ?? '').trim();

const normalizeMobile = (value) => toTrimmedString(value).replace(/\D/g, '');

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Organization name is required')
    .matches(
      /^[a-zA-Z0-9\s\-'.,&()]+$/,
      'Hospital name must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation'
    )
    .test('has-letter', 'Hospital name must contain at least one letter (not just numbers)', (value) => {
      if (!value) return true;
      return /[a-zA-Z]/.test(value);
    }),
  email: Yup.string().nullable().notRequired().email('Invalid email address'),
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Phone must be 10 digits')
    .required('Phone is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string()
    .required('City is required')
    .matches(
      /^[a-zA-Z0-9\s\-'.,&()]+$/,
      'City must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation'
    )
    .test('has-letter', 'City must contain at least one letter (not just numbers)', (value) => {
      if (!value) return true;
      return /[a-zA-Z]/.test(value);
    }),
  state: Yup.string().required('State is required'),
  pincode: Yup.string()
    .matches(/^\d{6}$/, 'Pincode must be 6 digits')
    .required('Pincode is required'),
  adminName: Yup.string()
    .required('Admin name is required')
    .matches(
      /^[a-zA-Z0-9\s\-'.,&()]+$/,
      'Admin name must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation'
    )
    .test('has-letter', 'Admin name must contain at least one letter (not just numbers)', (value) => {
      if (!value) return true;
      return /[a-zA-Z]/.test(value);
    }),
  adminEmail: Yup.string()
    .email('Invalid email address')
    .required('Admin email is required'),
  adminMobile: Yup.string()
    .matches(/^\d{10}$/, 'Mobile must be 10 digits')
    .required('Admin mobile is required'),
});

const EditHospitalOrganizationDialog = ({ open, onClose, organizationId, onSaved }) => {
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [includeGST, setIncludeGST] = useState(false);
  const [gstPercentage, setGstPercentage] = useState('');
  const [activeSubscriptionId, setActiveSubscriptionId] = useState(null);
  const [adminUserId, setAdminUserId] = useState(null);

  const [
    enableOpdConsultationWaiver,
    setEnableOpdConsultationWaiver,
  ] = useState(true);

  const [
    opdConsultationWaiverDays,
    setOpdConsultationWaiverDays,
  ] = useState(15);

  const originalAdminRef = useRef({ email: '', mobile: '' });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      adminName: '',
      adminEmail: '',
      adminMobile: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleSave(values);
    },
  });

  const validateGST = () => {
    if (includeGST && gstPercentage && gstPercentage.trim() !== '') {
      const gstValue = parseFloat(gstPercentage);
      if (isNaN(gstValue) || gstValue < 0 || gstValue > 100) {
        return 'GST percentage must be between 0 and 100';
      }
    }
    return null;
  };

  const findAdminStaff = (staffList) => {
    const list = Array.isArray(staffList) ? staffList : [];
    return (
      list.find((s) => (s.userRole || s.role || '').toLowerCase() === 'admin') || null
    );
  };

  useEffect(() => {
    if (!open || !organizationId) return;

    const loadOrganization = async () => {
      setLoadingOrg(true);
      setIncludeGST(false);
      setGstPercentage('');
      setActiveSubscriptionId(null);
      setAdminUserId(null);
      setEnableOpdConsultationWaiver(true);
      setOpdConsultationWaiverDays(15);
      originalAdminRef.current = { email: '', mobile: '' };

      try {
        const [org, activeSub, staffList] = await Promise.all([
          getHospitalOrganizationById(organizationId),
          getActiveSubscription(organizationId).catch(() => null),
          getAllStaff().catch(() => []),
        ]);

        const adminStaff = findAdminStaff(staffList);
        const admin = adminStaff
          ? {
              fullName: adminStaff.name,
              name: adminStaff.name,
              email: adminStaff.email,
              mobile: adminStaff.mobile,
              userId: adminStaff.userId,
            }
          : null;

        const adminEmail = toTrimmedString(admin?.email);
        const adminMobile = normalizeMobile(admin?.mobile);
        const adminName = toTrimmedString(admin?.fullName || admin?.name);

        originalAdminRef.current = {
          email: adminEmail,
          mobile: adminMobile,
        };
        setAdminUserId(admin?.userId || adminStaff?.userId || null);

        formik.setValues({
          name: org?.name || '',
          email: org?.email || '',
          phone: normalizeMobile(org?.phone),
          address: org?.address || '',
          city: org?.city || '',
          state: org?.state || '',
          pincode: org?.pincode || '',
          adminName,
          adminEmail,
          adminMobile,
        });
        formik.setTouched({});
        formik.setErrors({});

        if (activeSub) {
          setActiveSubscriptionId(activeSub.id);
          const hasGst =
            activeSub.includeGST === true ||
            activeSub.include_gst === true ||
            activeSub.gstPercentage != null ||
            activeSub.gst_percentage != null;
          setIncludeGST(hasGst);
          const gstVal = activeSub.gstPercentage ?? activeSub.gst_percentage;
          setGstPercentage(gstVal != null && gstVal !== '' ? String(gstVal) : '');

          // OPD consultation waiver settings
          setEnableOpdConsultationWaiver(
            activeSub.enableOpdConsultationWaiver ??
            activeSub.enable_opd_consultation_waiver ??
            true
          );

          setOpdConsultationWaiverDays(
            activeSub.opdConsultationWaiverDays ??
            activeSub.opd_consultation_waiver_days ??
            15
          );

        }
      } catch (err) {
        console.error('Error loading hospital organization:', err);
        toast.error('Failed to load hospital details');
        onClose?.();
      } finally {
        setLoadingOrg(false);
      }
    };

    loadOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId]);

  const getOriginalAdminSnapshot = () => ({
    email: toTrimmedString(originalAdminRef.current?.email),
    mobile: normalizeMobile(originalAdminRef.current?.mobile),
  });

  const checkDuplicates = async (values) => {
    const errors = {};
    const excludeOrgId = organizationId;
    const originalAdmin = getOriginalAdminSnapshot();
    const adminEmailChanged =
      toTrimmedString(values.adminEmail).toLowerCase() !== originalAdmin.email.toLowerCase();
    const adminMobileChanged =
      normalizeMobile(values.adminMobile) !== originalAdmin.mobile;

    try {
      if (values.name && toTrimmedString(values.name) !== '') {
        try {
          const hospitalNameExists = await checkHospitalNameExists(values.name, excludeOrgId);
          if (hospitalNameExists) {
            errors.name = 'A hospital organization with this name already exists';
          }
        } catch (error) {
          console.error('Error checking hospital name:', error);
        }
      }

      if (values.email && toTrimmedString(values.email) !== '') {
        try {
          const hospitalEmailExists = await checkHospitalEmailExists(values.email, excludeOrgId);
          if (hospitalEmailExists) {
            errors.email =
              'This email is already registered with another hospital organization';
          }
        } catch (error) {
          console.error('Error checking hospital email:', error);
        }
      }

      try {
        const hospitalPhoneExists = await checkHospitalPhoneExists(values.phone, excludeOrgId);
        if (hospitalPhoneExists) {
          errors.phone =
            'This phone number is already registered with another hospital organization';
        }
      } catch (error) {
        console.error('Error checking hospital phone:', error);
      }

      if (values.adminEmail && toTrimmedString(values.adminEmail) !== '' && adminEmailChanged) {
        try {
          const adminEmailExists = await checkAdminEmailExists(
            values.adminEmail,
            adminUserId
          );
          if (adminEmailExists) {
            errors.adminEmail = 'This email is already registered with another user';
          }
        } catch (error) {
          console.error('Error checking admin email:', error);
        }
      }

      if (
        values.adminMobile &&
        normalizeMobile(values.adminMobile) !== '' &&
        adminMobileChanged
      ) {
        try {
          const adminMobileExists = await checkAdminMobileExists(
            values.adminMobile,
            adminUserId
          );
          if (adminMobileExists) {
            errors.adminMobile =
              'This mobile number is already registered with another user';
          }
        } catch (error) {
          console.error('Error checking admin mobile:', error);
        }
      }

      if (Object.keys(errors).length > 0) {
        Object.keys(errors).forEach((key) => {
          formik.setFieldError(key, errors[key]);
        });
        toast.error('Please fix the duplicate values before proceeding');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error('Error validating data. Please try again.');
      return false;
    }
  };

  const updateAdminDetails = async (values) => {
    if (!adminUserId) {
      console.warn('No admin userId found — admin details were not updated');
      return;
    }

    const adminEmail = toTrimmedString(values.adminEmail);
    const adminMobile = normalizeMobile(values.adminMobile);

    let existingPassword = '';
    try {
      const staffDetail = await getStaffDetailByUserId(adminUserId);
      existingPassword = staffDetail?.password ? String(staffDetail.password) : '';
    } catch (staffLoadError) {
      console.warn('Could not load existing staff password for admin update:', staffLoadError);
    }

    await updateStaffByUserId(adminUserId, {
      name: toTrimmedString(values.adminName),
      email: adminEmail,
      mobile: adminMobile,
      userRole: 'admin',
      departmentId: null,
      departmentName: null,
      organizationId: Number(organizationId),
      password: existingPassword,
      joiningDate: new Date().toISOString().slice(0, 10),
    });

    await updatePortalUser({
      id: adminUserId,
      full_name: toTrimmedString(values.adminName),
      email: adminEmail,
      mobile: adminMobile,
      user_role: 'admin',
      department: '',
      organization_id: Number(organizationId),
      ...(existingPassword ? { user_pwd: existingPassword } : {}),
    });
  };

  const handleSave = async (values) => {
    const gstError = validateGST();
    if (gstError) {
      toast.error(gstError);
      return;
    }

    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      return;
    }

    const noDuplicates = await checkDuplicates(values);
    if (!noDuplicates) return;

    setSaving(true);
    try {
      const organizationData = {
        name: values.name,
        email: toTrimmedString(values.email) || null,
        phone: normalizeMobile(values.phone),
        address: values.address,
        city: values.city,
        state: values.state,
        pincode: values.pincode,
      };

      await updateHospitalOrganization(organizationId, organizationData);

      if (activeSubscriptionId) {
        try {
          await updateSubscriptionHistoryGst(activeSubscriptionId, {
            includeGST,
            gstPercentage: includeGST ? gstPercentage : null,
          });
        } catch (gstUpdateError) {
          console.error('Error updating subscription GST:', gstUpdateError);
          toast.warning(
            'Hospital details saved but GST settings could not be updated. Please contact support.'
          );
        }
      }

      if (activeSubscriptionId) {
        await updateSubscriptionHistoryOpdConsultationWaiver(
          activeSubscriptionId,
          {
            enableOpdConsultationWaiver,
            opdConsultationWaiverDays,
          }
        );
      }

      try {
        await updateAdminDetails(values);
      } catch (adminError) {
        console.error('Error updating admin details:', adminError);
        toast.warning(
          'Hospital details saved but admin details could not be updated. Please contact support.'
        );
      }

      syncSessionUserAfterHospitalEdit({
        organizationName: values.name,
        adminName: toTrimmedString(values.adminName),
      });

      toast.success('Hospital details updated successfully');
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error updating hospital organization:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update hospital details';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={HOSPITAL_FORM_DIALOG_PAPER_SX}>
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Edit Hospital Details</DialogTitle>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "0 0 auto",
          overflow: "visible",
        }}
      >
        <DialogContent
          sx={{
            ...FIT_CONTENT_DIALOG_CONTENT_SX,
            height: '70vh',
            maxHeight: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {loadingOrg ? (
            <Grid container justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container spacing={1.5} sx={{ pt: 0 }}>
              <Grid item xs={12}>
                <FormSectionHeader title="Hospital Information" sx={{ mb: 0.5 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hospital Name"
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue('name', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hospital Email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[a-zA-Z0-9._+-@]*$/.test(value)) {
                      formik.setFieldValue('email', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formik.values.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue('phone', value);
                  }}
                  onBlur={formik.handleBlur}
                  inputProps={{ maxLength: 10 }}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={1}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formik.values.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue('city', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="state"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  {...DENSE_FIELD_PROPS}
                  SelectProps={{
                    MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                  }}
                >
                  <MenuItem value="">
                    <em>Select State</em>
                  </MenuItem>
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formik.values.pincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    formik.setFieldValue('pincode', value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={formik.touched.pincode && formik.errors.pincode}
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>

              {/* GST Section */}
              <Grid item xs={12}>
                <FormSectionHeader title="GST (Goods and Services Tax) Settings" sx={{ mb: 0.5 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeGST}
                      onChange={(e) => {
                        setIncludeGST(e.target.checked);
                        if (!e.target.checked) {
                          setGstPercentage('');
                        }
                      }}
                    />
                  }
                  label="Include GST in subscription (optional)"
                />
                {includeGST && (
                  <TextField
                    fullWidth
                    label="GST Percentage (%)"
                    type="number"
                    value={gstPercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setGstPercentage(value);
                      }
                    }}
                    error={
                      includeGST &&
                      gstPercentage &&
                      (parseFloat(gstPercentage) < 0 || parseFloat(gstPercentage) > 100)
                    }
                    helperText={
                      includeGST &&
                      gstPercentage &&
                      (parseFloat(gstPercentage) < 0 || parseFloat(gstPercentage) > 100)
                        ? 'GST percentage must be between 0 and 100'
                        : 'Enter GST percentage (e.g., 18 for 18%)'
                    }
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.01,
                    }}
                    sx={{ mt: 1, maxWidth: 280 }}
                    {...DENSE_FIELD_PROPS}
                  />
                )}
              </Grid>

              <Grid item xs={6}>
                  <FormControlLabel
                  control={
                    <Switch
                      checked={enableOpdConsultationWaiver}
                      onChange={(e) =>
                        setEnableOpdConsultationWaiver(e.target.checked)
                      }
                    />
                  }
                  label="Enable OPD Consultation Follow-up Waiver"
                />

                {enableOpdConsultationWaiver && (
                  <TextField
                  label="OPD Consultation Follow-up Days"
                  type="number"
                  value={opdConsultationWaiverDays}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "" || /^\d+$/.test(value)) {
                      setOpdConsultationWaiverDays(value);
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                  fullWidth
                />
                )}
                </Grid>

                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ mb: 0.75 }}
                      >
                        OPD Consultation Follow-up Waiver
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.45 }}
                      >
                        <strong>Enabled:</strong>{" "}
                        {enableOpdConsultationWaiver ? "Yes" : "No"}
                      </Typography>

                      {enableOpdConsultationWaiver && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.45 }}
                        >
                          <strong>Follow-up Waiver Days:</strong>{" "}
                          {opdConsultationWaiverDays} days
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

              <Grid item xs={12}>
                <FormSectionHeader title="Admin Details" sx={{ mb: 0.5 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Name"
                  name="adminName"
                  value={formik.values.adminName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(value)) {
                      formik.setFieldValue('adminName', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.adminName && Boolean(formik.errors.adminName)}
                  helperText={formik.touched.adminName && formik.errors.adminName}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Email"
                  name="adminEmail"
                  type="email"
                  value={formik.values.adminEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[^\s@]*@?[^\s@]*$/.test(value)) {
                      formik.setFieldValue('adminEmail', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.adminEmail && Boolean(formik.errors.adminEmail)}
                  helperText={formik.touched.adminEmail && formik.errors.adminEmail}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Admin Mobile"
                  name="adminMobile"
                  value={formik.values.adminMobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue('adminMobile', value);
                  }}
                  onBlur={formik.handleBlur}
                  inputProps={{ maxLength: 10 }}
                  error={formik.touched.adminMobile && Boolean(formik.errors.adminMobile)}
                  helperText={formik.touched.adminMobile && formik.errors.adminMobile}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={handleClose} disabled={saving || loadingOrg} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" sx={FORM_SUBMIT_BUTTON_SX} disabled={saving || loadingOrg}>
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditHospitalOrganizationDialog;
